/*
 * One row of the justified rows of an image list (EventgalleryImageList.js). The row takes
 * images at the height it aims for until the next one does not fit; an image which makes
 * the row less than a fifth too wide still goes in (the row gets a little lower then), the
 * rule of 6.x; a row of one image of the list (not beside a first image over several rows,
 * whose heights the list solves together) also takes the next one when that brings it closer
 * to its height than the lone image stretched over the whole width. Then the row is drawn
 * at the height at which its images, whole and in their own proportions, fill the width
 * exactly - or, as the last row of a list which does not fill its last row, at the height it
 * aimed for.
 *
 * An image is anything with a ratio (width divided by height); the row computes boxes and
 * writes nothing into the page.
 */
export default class Row {

    /**
     * @param {{maxWidth: number, maxHeight: number, gap?: number, joinsLoneImage?: boolean}} options
     *        the width to fill, the height the row aims for, the gap between two images, all in
     *        CSS pixels, and whether a row of one image takes a next one which brings it closer
     */
    constructor(options) {
        this.maxWidth = options.maxWidth;
        this.maxHeight = options.maxHeight;
        this.gap = options.gap || 0;
        this.joinsLoneImage = options.joinsLoneImage || false;
        this.images = [];
        // the width of the images at the height the row aims for, without the gaps
        this.width = 0;
    }

    /**
     * @param {{ratio: number}} image
     * @returns {boolean} false when the row is full; the image is not taken then
     */
    add(image) {
        const imageWidth = image.ratio * this.maxHeight;
        const used = this.width + this.images.length * this.gap;
        let fits = this.images.length === 0 || used + imageWidth <= this.maxWidth;

        if (!fits && Math.abs(this.maxWidth - used - imageWidth) / this.maxWidth < 0.2) {
            fits = true;
        }
        // a lone image would be stretched over the whole width: it takes the next one when the
        // row comes closer to the height it aims for with it
        if (!fits && this.joinsLoneImage && this.images.length === 1) {
            const off = (height) => Math.max(height, this.maxHeight) / Math.min(height, this.maxHeight);
            fits = off((this.maxWidth - this.gap) / (this.ratioSum() + image.ratio)) < off(this.justifiedHeight());
        }
        if (fits) {
            this.images.push(image);
            this.width += imageWidth;
        }
        return fits;
    }

    /**
     * @returns {number} the sum of the proportions of the images of the row
     */
    ratioSum() {
        return this.images.reduce((sum, image) => sum + image.ratio, 0);
    }

    /**
     * @param {number} [width] the width to fill, the one of the row by default
     * @returns {number} the height at which the images fill the width exactly
     */
    justifiedHeight(width = this.maxWidth) {
        return (width - (this.images.length - 1) * this.gap) / this.ratioSum();
    }

    /**
     * @param {number} [width]
     * @returns {boolean} whether the images fit into the width at the height the row aims for
     */
    fitsAtItsHeight(width = this.maxWidth) {
        return this.width + (this.images.length - 1) * this.gap <= width;
    }
}
